import React from 'react';
import { motion } from 'framer-motion';
import { FadeInWhenVisible, SectionHeader } from './SectionWrapper';
import { PERSONAL } from '../data/portfolio';

const ABOUT_TAGS = [
  'Distributed Systems', 'Event-Driven Design', 'Kafka Streaming',
  'Microservices', 'Performance Engineering', 'API Design',
  'Team Leadership', 'System Design',
];

const PROFILE_ITEMS = [
  { icon: '📍', label: 'Location', value: PERSONAL.location },
  { icon: '💼', label: 'Current Focus', value: `${PERSONAL.role} / ${PERSONAL.roleAlt}` },
  { icon: '🎯', label: 'Specialization', value: 'Microservices + Kafka' },
  { icon: '✉', label: 'Email', value: PERSONAL.email },
  { icon: '🔗', label: 'LinkedIn', value: 'linkedin.com/in/nshw' },
];

export default function About({ isDark }) {
  return (
    <section
      id="about"
      className={`py-24 ${isDark ? 'bg-navy-800' : 'bg-gray-50'}`}
    >
      <div className="max-w-6xl mx-auto px-6">
        <SectionHeader tag="// 01. about me" heading="The engineer behind<br/>the architecture" isDark={isDark} />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Story */}
          <FadeInWhenVisible delay={0.1}>
            <div className="space-y-5">
              <p className={`leading-relaxed text-base ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                I'm{' '}
                <strong className="font-semibold" style={{ color: 'var(--gold)' }}>
                  {PERSONAL.name}
                </strong>
                , a Senior Java Backend Developer based in{' '}
                <strong className={`font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  {PERSONAL.location}
                </strong>
                , with 4+ years of hands-on experience building and scaling enterprise-grade
                systems across fintech, edtech, and retail domains.
              </p>
              <p className={`leading-relaxed text-base ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                My expertise spans{' '}
                <strong className="font-semibold" style={{ color: 'var(--gold)' }}>
                  Microservices architecture
                </strong>
                ,{' '}
                <strong className="font-semibold" style={{ color: 'var(--gold)' }}>
                  event-driven systems with Apache Kafka
                </strong>
                , and building distributed backends that remain resilient under high load.
                I've led performance optimization efforts that reduced latency by up to 30%
                and mentored teams of junior engineers.
              </p>
              <p className={`leading-relaxed text-base ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                I think in{' '}
                <strong className={`font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  systems
                </strong>{' '}
                — every design decision I make considers scale, fault tolerance, and
                operational simplicity. I thrive in environments where backend correctness
                and API design rigor actually matter.
              </p>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 pt-3">
                {ABOUT_TAGS.map((tag, i) => (
                  <motion.span
                    key={tag}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 + i * 0.05 }}
                    whileHover={{ scale: 1.05, borderColor: 'var(--gold)' }}
                    className={`font-mono text-xs px-3 py-1.5 rounded-full border cursor-default transition-colors duration-200 ${
                      isDark
                        ? 'text-gray-400 border-navy-300/60 hover:text-gold'
                        : 'text-gray-500 border-gray-300 hover:text-gold-muted hover:border-gold-muted/50'
                    }`}
                  >
                    {tag}
                  </motion.span>
                ))}
              </div>
            </div>
          </FadeInWhenVisible>

          {/* Profile card */}
          <FadeInWhenVisible delay={0.2}>
            <motion.div
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300 }}
              className={`rounded-xl border p-7 ${
                isDark
                  ? 'bg-navy-600 border-navy-300/50'
                  : 'bg-white border-gray-200 shadow-sm'
              }`}
            >
              <p className={`font-mono text-xs tracking-widest mb-6 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                // quick profile
              </p>
              <div className="space-y-5">
                {PROFILE_ITEMS.map((item, i) => (
                  <React.Fragment key={item.label}>
                    <div className="flex items-start gap-4">
                      <div
                        className="w-9 h-9 rounded-lg flex items-center justify-center text-sm flex-shrink-0"
                        style={{
                          background: 'rgba(232,184,75,0.1)',
                          border: '1px solid rgba(232,184,75,0.2)',
                        }}
                      >
                        {item.icon}
                      </div>
                      <div>
                        <div className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                          {item.label}
                        </div>
                        <div className={`font-mono text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {item.value}
                        </div>
                      </div>
                    </div>
                    {i < PROFILE_ITEMS.length - 1 && (
                      <div className={`border-b ${isDark ? 'border-navy-400/50' : 'border-gray-100'}`} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </motion.div>
          </FadeInWhenVisible>
        </div>
      </div>
    </section>
  );
}
